import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { ApiKeyProvider } from './contexts/ApiKeyContext';
import { AIAgentProvider } from './contexts/AIAgentContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { ToastProvider } from './contexts/ToastContext';
import { Layout } from './components/Layout';
import { PlanLockedGate } from './components/planLock/PlanLockedGate';
import { ScrollToTop } from './components/ScrollToTop';
import { AuthGuard } from './components/AuthGuard';
import { AdminGuard } from './components/AdminGuard';
import { ProductQaGuard } from './components/ProductQaGuard';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { InvitationAcceptPage } from './pages/InvitationAcceptPage';
import { DashboardGate } from './pages/DashboardGate';
import { AccountLockedPage } from './pages/AccountLockedPage';
import { CameraBrowserPage } from './pages/CameraBrowserPage';
import { ProfilePage } from './pages/ProfilePage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { ApiTestPage } from './pages/ApiTestPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import IntegrationDetailPage from './pages/IntegrationDetailPage';
import IntegrationShopifyDuplicatesPage from './pages/IntegrationShopifyDuplicatesPage';
// ProductSyncSelectionPage retired — sync selection is now in the product browser
import { SkuOverridesPage } from './pages/SkuOverridesPage';
import { StoreBundlesPage } from './pages/StoreBundlesPage';
import { GroupOverviewPage } from './pages/GroupOverviewPage';
import { AdminUserManagementPage } from './pages/AdminUserManagementPage';
import { AdminNotificationsPage } from './pages/AdminNotificationsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { AdminSystemPage } from './pages/AdminSystemPage';
import { AdminAuditLogPage } from './pages/AdminAuditLogPage';
import { AdminBillingPage } from './pages/AdminBillingPage';
import { AdminPlansPage } from './pages/AdminPlansPage';
import { AdminMapPricingManagementPage } from './pages/AdminMapPricingManagementPage';
import { AdminRebateManagementPage } from './pages/AdminRebateManagementPage';
import AdminStoreManagementPage from './pages/AdminStoreManagementPage';
import AdminStoreDetailPage from './pages/AdminStoreDetailPage';
import AdminOrganizationGroupsHubPage from './pages/AdminOrganizationGroupsHubPage';
import AdminPricingIngestionPage from './pages/AdminPricingIngestionPage';
import AdminCanonicalProductsPage from './pages/AdminCanonicalProductsPage';
import MapPricingDataViewPage from './pages/MapPricingDataViewPage';
import StoreShopifyPricingAuditPage from './pages/StoreShopifyPricingAuditPage';
import PriceHistoryPage from './pages/PriceHistoryPage';
import StoreChangeRecapPage from './pages/StoreChangeRecapPage';
import StoreBulkAiPage from './pages/StoreBulkAiPage';
import StoreSerialsPage from './pages/StoreSerialsPage';
import ProductComparePage from './pages/ProductComparePage';
import StorePricingPreferencesPage from './pages/StorePricingPreferencesPage';
import BrandVendorsDirectoryPage from './pages/brandsVendors/BrandVendorsDirectoryPage';
import BrandVendorsBrandPage from './pages/brandsVendors/BrandVendorsBrandPage';
import BrandVendorsDashboardPage from './pages/brandsVendors/BrandVendorsDashboardPage';
import BrandVendorsLogoLibraryPage from './pages/brandsVendors/BrandVendorsLogoLibraryPage';
import BrandVendorsOrganizationsPage from './pages/brandsVendors/BrandVendorsOrganizationsPage';
import BrandVendorsApprovalsPage from './pages/brandsVendors/BrandVendorsApprovalsPage';
import BrandVendorsDealerListsPage from './pages/brandsVendors/BrandVendorsDealerListsPage';
import BrandVendorsPriceClocksPage from './pages/brandsVendors/BrandVendorsPriceClocksPage';
import BrandVendorsDealerAuthorizationsPage from './pages/brandsVendors/BrandVendorsDealerAuthorizationsPage';
import VendorSignupPage from './pages/brandsVendors/VendorSignupPage';
import AdminStagedProductsPage from './pages/AdminStagedProductsPage';
import AdminProductSubmissionsPage from './pages/AdminProductSubmissionsPage';
import AdminProductIssuesPage from './pages/AdminProductIssuesPage';
import AdminCatalogPriceChangesPage from './pages/AdminCatalogPriceChangesPage';
import AdminPricingFreshnessPage from './pages/AdminPricingFreshnessPage';
import AdminStoreSyncOverviewPage from './pages/AdminStoreSyncOverviewPage';

import { VendorBrandMapManagementPage } from './pages/VendorBrandMapManagementPage';
import { VendorBrandRebateManagementPage } from './pages/VendorBrandRebateManagementPage';
import { VendorCarryRequestsPage } from './pages/VendorCarryRequestsPage';
import { VendorDealerPricingPage } from './pages/VendorDealerPricingPage';
import { DataIngestionPage } from './pages/DataIngestionPage';
import { BillingManagementPage } from './pages/BillingManagementPage';
import AdminJobsPage from './pages/AdminJobsPage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import ApiDocsPage from './pages/ApiDocsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import CompliancePage from './pages/CompliancePage';
import TermsPage from './pages/TermsPage';
import ProductDemosPage from './pages/ProductDemosPage';
import NotFoundPage from './pages/NotFoundPage';
import StoreSettingsPage from './pages/StoreSettingsPage';
import TeamManagementPage from './pages/TeamManagementPage';
import SalesReportPage from './pages/SalesReportPage';
import RebateReportPage from './pages/RebateReportPage';
import OnboardingSelectTypePage from './pages/OnboardingSelectTypePage';
import TermsAcceptancePage from './pages/TermsAcceptancePage';
import OnboardingGuard from './components/OnboardingGuard';
import { StoreOrganizationSetupPage } from './pages/StoreOrganizationSetupPage';
import { OnboardingStoreLocationsPage } from './pages/OnboardingStoreLocationsPage';
import OnboardingSetupGroupPage from './pages/OnboardingSetupGroupPage';
import OnboardingGroupPendingPage from './pages/OnboardingGroupPendingPage';
import { VendorOrganizationSetupPage } from './pages/VendorOrganizationSetupPage';
import PublicLayout from './components/PublicLayout';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { ForcedRefreshDialog, UpdateProvider } from './components/appUpdate';
import { ConditionalAnalytics } from './components/ConditionalAnalytics';
import { ConditionalMarketing } from './components/ConditionalMarketing';
import { appTheme } from './theme/appTheme';
import { RoutedErrorBoundary } from './components/AppErrorBoundary';

// Help Center pages carry the markdown renderer; load them only when visited.
const HelpCenterPage = React.lazy(() => import('./pages/HelpCenterPage'));
const HelpArticlePage = React.lazy(() => import('./pages/HelpArticlePage'));

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/** Preserves `#application` (and any other hash) across the old Brand Metrics path. */
const VendorMetricsRedirect: React.FC = () => {
  const { hash } = useLocation();
  return <Navigate to={`/app/dashboard${hash}`} replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <ToastProvider>
        <LocaleProvider>
          <AuthProvider>
            <ImpersonationProvider>
              <OrganizationProvider>
              <AIAgentProvider>
                <Router>
            <RoutedErrorBoundary>
            <UpdateProvider>
            <ScrollToTop />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              {/* Legacy / shared marketing links use /signup; same page as /register. */}
              <Route path="/signup" element={<RegisterPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              {/* The team-invitation email link. Unauthenticated by design — see InvitationAcceptPage. */}
              <Route path="/invitations/accept" element={<InvitationAcceptPage />} />
              <Route path="/vendor-signup" element={<VendorSignupPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/docs" element={<ApiDocsPage />} />
              {/* Help Center (flag `help_center`): the customer docs; a 404 while the flag is off. */}
              <Route path="/help" element={<React.Suspense fallback={null}><HelpCenterPage /></React.Suspense>} />
              <Route path="/help/:category" element={<React.Suspense fallback={null}><HelpCenterPage /></React.Suspense>} />
              <Route path="/help/:category/:slug" element={<React.Suspense fallback={null}><HelpArticlePage /></React.Suspense>} />
              {/* Support folded into Contact; redirect so existing links survive. */}
              <Route path="/support" element={<Navigate to="/contact" replace />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/compliance" element={<CompliancePage />} />
              <Route path="/terms" element={<TermsPage />} />
              {/* Folded into Features; redirect so existing links keep working. */}
              <Route path="/integrations" element={<Navigate to="/features" replace />} />
              <Route path="/product-demos/:id?" element={<PublicLayout><ProductDemosPage /></PublicLayout>} />
              
              {/* Onboarding Routes (Protected but don't require completed onboarding) */}
              <Route path="/onboarding/terms" element={
                <AuthGuard>
                  <TermsAcceptancePage />
                </AuthGuard>
              } />
              <Route path="/onboarding/select-type" element={
                <AuthGuard>
                  <OnboardingSelectTypePage />
                </AuthGuard>
              } />
              <Route path="/onboarding/setup-store" element={
                <AuthGuard>
                  <StoreOrganizationSetupPage />
                </AuthGuard>
              } />
              <Route path="/onboarding/store-locations" element={
                <AuthGuard>
                  <OnboardingStoreLocationsPage />
                </AuthGuard>
              } />
              <Route path="/onboarding/setup-vendor" element={
                <AuthGuard>
                  <VendorOrganizationSetupPage />
                </AuthGuard>
              } />
              <Route path="/onboarding/setup-group" element={
                <AuthGuard>
                  <OnboardingSetupGroupPage />
                </AuthGuard>
              } />
              <Route path="/onboarding/group-pending" element={
                <AuthGuard>
                  <OnboardingGroupPendingPage />
                </AuthGuard>
              } />

              {/* Protected Routes */}
              {/* Canonical app root */}
              <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />

              <Route path="/app/account-locked" element={
                <AuthGuard>
                  <AccountLockedPage />
                </AuthGuard>
              } />

              <Route path="/app/dashboard" element={
                <AuthGuard>
                  <OnboardingGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <DashboardGate />
                      </Layout>
                    </ApiKeyProvider>
                  </OnboardingGuard>
                </AuthGuard>
              } />
              
              <Route path="/app/products/:productId?" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <CameraBrowserPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />
              {/* Legacy route - redirect to products */}
              <Route path="/app/cameras" element={<Navigate to="/app/products" replace />} />

              {/* Reached from the compare bar in the product browser (flag product_comparison); no nav entry of its own. */}
              <Route path="/app/compare" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <ProductComparePage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />
              
              <Route path="/app/profile" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <ProfilePage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />

              <Route path="/app/notifications" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <NotificationsPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />
              
              <Route path="/app/billing" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <BillingManagementPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />
              
              <Route path="/app/store-settings" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <StoreSettingsPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />
              
              <Route path="/app/team-management" element={
                <AuthGuard>
                  <OnboardingGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <TeamManagementPage />
                      </Layout>
                    </ApiKeyProvider>
                  </OnboardingGuard>
                </AuthGuard>
              } />
              <Route path="/app/sales-report" element={
                <AuthGuard>
                  <OnboardingGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <SalesReportPage />
                      </Layout>
                    </ApiKeyProvider>
                  </OnboardingGuard>
                </AuthGuard>
              } />
              <Route path="/app/rebate-report" element={
                <AuthGuard>
                  <OnboardingGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <PlanLockedGate feature="rebateTracking">
                          <RebateReportPage />
                        </PlanLockedGate>
                      </Layout>
                    </ApiKeyProvider>
                  </OnboardingGuard>
                </AuthGuard>
              } />
              
              <Route path="/app/api-keys" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <ApiKeysPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />
              
              {/* Legacy MAP pricing pages were superseded by the store MAP data view. */}
              <Route path="/app/map-pricing" element={<Navigate to="/app/map-pricing-data" replace />} />
              
              <Route path="/app/api-test" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <ApiTestPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />
              
              <Route path="/app/integrations" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <IntegrationsPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />
              
              <Route path="/app/integrations/:id" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <IntegrationDetailPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />

              <Route path="/app/integrations/:id/shopify-duplicates" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <IntegrationShopifyDuplicatesPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />
              
              {/* Legacy route - sync selection now lives in the product browser */}
              <Route path="/app/sync-selection" element={<Navigate to="/app/products" replace />} />

              <Route path="/app/sku-overrides" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <SkuOverridesPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />

              {/* The group pages live in the store shell; the nav shows them
                  only to someone who manages a group. */}
              <Route path="/app/group" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <GroupOverviewPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />

              <Route path="/app/store-bundles" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <StoreBundlesPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />
              
              <Route path="/app/map-management" element={<Navigate to="/app/map-pricing-data" replace />} />
              
              {/* Admin Routes */}
              {/* Canonical admin landing */}
              <Route path="/app/admin" element={<Navigate to="/app/admin/brands-vendors" replace />} />
              <Route path="/app/admin/products/:productId?" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminCanonicalProductsPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />
              {/* Legacy camera route */}
              <Route path="/app/admin/cameras" element={<Navigate to="/app/admin/products" replace />} />
              
              <Route path="/app/admin/users" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminUserManagementPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />

              <Route path="/app/admin/stores" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminStoreManagementPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />

              <Route path="/app/admin/stores/:orgId" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminStoreDetailPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />

              <Route path="/app/admin/organization-groups" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminOrganizationGroupsHubPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />

              <Route path="/app/admin/organizations" element={<Navigate to="/app/admin/brands-vendors/organizations" replace />} />

              <Route path="/app/admin/store-sync-health" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminStoreSyncOverviewPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />
              
              <Route path="/app/admin/data-ingestion" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <DataIngestionPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />
              
              <Route path="/app/admin/staged-products/:id?" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminStagedProductsPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />

              <Route path="/app/admin/product-submissions" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminProductSubmissionsPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />

              <Route path="/app/store/staged-products/:id?" element={
                <AuthGuard>
                  <ProductQaGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminStagedProductsPage />
                      </Layout>
                    </ApiKeyProvider>
                  </ProductQaGuard>
                </AuthGuard>
              } />

              <Route path="/app/admin/product-issues/:issueId?" element={
                <AuthGuard>
                  <AdminGuard>
                    <Layout>
                      <AdminProductIssuesPage />
                    </Layout>
                  </AdminGuard>
                </AuthGuard>
              } />

              <Route path="/app/admin/catalog-price-changes" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminCatalogPriceChangesPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />

              <Route path="/app/admin/pricing-freshness" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminPricingFreshnessPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />

              <Route path="/app/admin/jobs" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminJobsPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />
              
                     <Route path="/app/admin/billing" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <AdminBillingPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />

                     <Route path="/app/admin/brands-vendors/dealer-lists" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <BrandVendorsDealerListsPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />

                     <Route path="/app/admin/brands-vendors/price-clocks" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <BrandVendorsPriceClocksPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />

                     <Route path="/app/admin/brands-vendors/dealer-authorizations" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <BrandVendorsDealerAuthorizationsPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />

                     <Route path="/app/admin/plans" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <AdminPlansPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />

                     <Route path="/app/admin/brands-vendors/logos" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <BrandVendorsLogoLibraryPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />
                     <Route path="/app/admin/brands-vendors/organizations" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <BrandVendorsOrganizationsPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />
                     <Route path="/app/admin/group-applications" element={<Navigate to="/app/admin/organization-groups?tab=applications" replace />} />
                     <Route path="/app/admin/brands-vendors/approvals" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <BrandVendorsApprovalsPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />
                     <Route path="/app/admin/brands-vendors/locator-sources" element={<Navigate to="/app/admin/brands-vendors/dealer-lists" replace />} />
                     <Route path="/app/admin/brands-vendors/brands/:brandId/dashboard" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <BrandVendorsDashboardPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />
                     <Route path="/app/admin/brands-vendors/brands/:brandId" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <BrandVendorsBrandPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />
                     <Route path="/app/admin/brands-vendors" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <BrandVendorsDirectoryPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />

                     <Route path="/app/admin/vendors" element={<Navigate to="/app/admin/brands-vendors" replace />} />
                     <Route path="/app/admin/brands" element={<Navigate to="/app/admin/brands-vendors" replace />} />
                     <Route path="/app/admin/vendor-metrics" element={<Navigate to="/app/admin/brands-vendors" replace />} />
                     <Route path="/app/admin/map-pricing" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <AdminMapPricingManagementPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />
                     <Route path="/app/admin/rebates" element={
                       <AuthGuard>
                         <AdminGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <AdminRebateManagementPage />
                             </Layout>
                           </ApiKeyProvider>
                         </AdminGuard>
                       </AuthGuard>
                     } />

                     {/* Consolidated vendor dashboard — all vendor paths redirect to /app/dashboard.
                         Brand Metrics is folded into Dashboard too; its hash is preserved so old
                         bookmarks and in-flight notifications to #application still land on the
                         Application card. */}
                     <Route path="/app/vendor-dashboard" element={<Navigate to="/app/dashboard" replace />} />
                     <Route path="/app/vendor-brand-dashboard" element={<Navigate to="/app/dashboard" replace />} />
                     <Route path="/app/vendor-brand-dashboard-old" element={<Navigate to="/app/dashboard" replace />} />
                     <Route path="/app/vendor-metrics" element={<VendorMetricsRedirect />} />

                     <Route path="/app/vendor-map-management" element={
                       <AuthGuard>
                         <OnboardingGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <VendorBrandMapManagementPage />
                             </Layout>
                           </ApiKeyProvider>
                         </OnboardingGuard>
                       </AuthGuard>
                     } />

                     <Route path="/app/vendor-rebate-management" element={
                       <AuthGuard>
                         <OnboardingGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <VendorBrandRebateManagementPage />
                             </Layout>
                           </ApiKeyProvider>
                         </OnboardingGuard>
                       </AuthGuard>
                     } />

                     <Route path="/app/vendor-carry-requests" element={
                       <AuthGuard>
                         <OnboardingGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <VendorCarryRequestsPage />
                             </Layout>
                           </ApiKeyProvider>
                         </OnboardingGuard>
                       </AuthGuard>
                     } />

                     <Route path="/app/vendor-dealer-pricing" element={
                       <AuthGuard>
                         <OnboardingGuard>
                           <ApiKeyProvider>
                             <Layout>
                               <VendorDealerPricingPage />
                             </Layout>
                           </ApiKeyProvider>
                         </OnboardingGuard>
                       </AuthGuard>
                     } />
              
              <Route path="/app/admin/notifications" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminNotificationsPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />

              {/* Consolidated System page (settings, data repair, normalization, privacy requests) */}
              <Route path="/app/admin/system" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminSystemPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />

              {/* Legacy routes - redirect to consolidated System page with appropriate tab */}
              <Route path="/app/admin/privacy-requests" element={<Navigate to="/app/admin/system?tab=privacy-requests" replace />} />

              <Route path="/app/admin/audit-logs" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminAuditLogPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />
              
              <Route path="/app/admin/settings" element={<Navigate to="/app/admin/system?tab=general" replace />} />
              <Route path="/app/admin/data-repair" element={<Navigate to="/app/admin/system?tab=data-repair" replace />} />
              <Route path="/app/admin/normalization" element={<Navigate to="/app/admin/system?tab=normalization" replace />} />

              <Route path="/app/admin/pricing-ingestion" element={
                <AuthGuard>
                  <AdminGuard>
                    <ApiKeyProvider>
                      <Layout>
                        <AdminPricingIngestionPage />
                      </Layout>
                    </ApiKeyProvider>
                  </AdminGuard>
                </AuthGuard>
              } />

              <Route path="/app/map-pricing-data" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <MapPricingDataViewPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />

              <Route path="/app/shopify-pricing-audit" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <StoreShopifyPricingAuditPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />

              <Route path="/app/price-history" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <PriceHistoryPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />

              <Route path="/app/change-recap" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <StoreChangeRecapPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />

              <Route path="/app/bulk-ai" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <PlanLockedGate feature="aiContentTools" orgFlag="bulk_ai_operations">
                        <StoreBulkAiPage />
                      </PlanLockedGate>
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />

              <Route path="/app/serials" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <PlanLockedGate feature="rebateTracking" orgFlag="serial_capture">
                        <StoreSerialsPage />
                      </PlanLockedGate>
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />

              <Route path="/app/pricing-preferences" element={
                <AuthGuard>
                  <ApiKeyProvider>
                    <Layout>
                      <StorePricingPreferencesPage />
                    </Layout>
                  </ApiKeyProvider>
                </AuthGuard>
              } />
              
              {/* Unknown routes render a 404 page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <CookieConsentBanner />
            <ForcedRefreshDialog />
            <ConditionalAnalytics />
            <ConditionalMarketing />
            </UpdateProvider>
            </RoutedErrorBoundary>
                </Router>
              </AIAgentProvider>
              </OrganizationProvider>
            </ImpersonationProvider>
          </AuthProvider>
        </LocaleProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
